"use client"

import React, { PropsWithChildren, Suspense, useMemo } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"

import { formatPrice } from "@/lib/helper"
import { BasketContext, BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"
import { Skeleton } from "@/components/ui/components"
import { BasketLineItemList } from "@/components/basket/overview/BasketLineItemList"
import { UbBasketLineItemsStoryblok } from "@/components/storyblok/component-types"

interface BasketLineItemsProps extends PropsWithChildren {
  basketContext: BasketContext
  blok: UbBasketLineItemsStoryblok
  context: string
}

export default function BasketLineItemsClient(props: BasketLineItemsProps) {
  const isoLocale = props.basketContext.isoLocale
  const isB2B = props.basketContext.isB2B
  const isAnonymous = props.basketContext.isAnonymous
  const blok = props.blok
  const preventLinkToProductDetails = props.context === "review"
  const { basketData, basketLineItems, basketPromotionCodes } = useUbStorefrontState()

  const basketTotalData: BasketTotalData = useMemo(() => {
    return determineBasketTotalData(isB2B, basketData, basketLineItems, basketPromotionCodes, isAnonymous, isoLocale)
  }, [basketData, basketLineItems, basketPromotionCodes, isB2B, isAnonymous, isoLocale])

  return (
    <div className={"flex w-full flex-col gap-2"}>
      <Suspense>
        <>
          {basketData &&
          (basketData?.data?.totalProductQuantity ?? 0) > 0 &&
          basketLineItems &&
          basketLineItems.length > 0 ? (
            <>
              <div className={"flex flex-row flex-wrap items-center justify-between"}>
                {blok?.title && blok.title.length > 0 && (
                  <p className={"text-sm font-bold sm:text-lg"}>
                    {blok.title.replace("$count", String(basketData?.data?.totalProductQuantity))}
                  </p>
                )}
                {!isB2B &&
                  basketTotalData.paymentCostAmountGross > 0 &&
                  blok?.handling_fee_difference_hint &&
                  blok.handling_fee_difference_hint.length > 0 && (
                    <p className={"text-xs text-primary"}>
                      {blok.handling_fee_difference_hint.replace(
                        "$noPaymentCostDifference",
                        formatPrice(isoLocale, basketTotalData.noPaymentCostDifference, basketTotalData.currency)
                      )}
                    </p>
                  )}
              </div>
              <div className={"grid w-full grid-cols-1 gap-y-2"}>
                <div className={"col-span-2"}>
                  <div className={"flex flex-col gap-2"}>
                    <BasketLineItemList
                      preventLinkToProductDetails={preventLinkToProductDetails}
                      isB2B={isB2B}
                      basketLineItems={basketLineItems}
                      sbLocale={props.basketContext.sbLocale}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {!basketData && (
                <div className="flex flex-col gap-4">
                  <Skeleton className="h-40 w-full" />
                  <Skeleton className="h-40 w-full" />
                </div>
              )}

              <div>{basketData && (basketData?.data?.totalProductQuantity ?? 0) < 1 && <p></p>}</div>
            </>
          )}
        </>
      </Suspense>
    </div>
  )
}
